import React from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  MenuItem,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import {
  addCurrencyRate,
  buildCurrencySavePayload,
  removeCurrencyRate,
  updateCurrencyRate,
} from '../../utils/settingsForm';
import { SUPPORTED_CURRENCIES } from '../../constants/appConstants';
import SettingsSectionCard from './SettingsSectionCard';

function CurrencySettingsSection({
  form,
  setField,
  onSave,
  onReset,
  loading,
  saving,
  error,
  success,
  setSuccess,
}) {
  const { t } = useTranslation();

  const updateRate = (index, key, value) => {
    setField('currencyRates', updateCurrencyRate(form.currencyRates, index, key, value));
  };

  const addRate = () => {
    setField('currencyRates', addCurrencyRate(form.currencyRates, form.defaultItemCurrency, form.checkCurrency));
  };

  const removeRate = (index) => {
    setField('currencyRates', removeCurrencyRate(form.currencyRates, index));
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>;
  }

  return (
    <SettingsSectionCard
      title={t('settings.sections.currency')}
      description={t('settings.sections.currencyHelp')}
      error={error}
      success={success}
      successMessage={t('settings.saved')}
      onCloseSuccess={() => setSuccess(false)}
    >
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label={t('settings.defaultItemCurrency')}
            value={form.defaultItemCurrency}
            onChange={(e) => setField('defaultItemCurrency', e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          >
            {SUPPORTED_CURRENCIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.code} — {t(`settings.currencies.${c.code}`, { defaultValue: c.name })}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('settings.checkCurrency')}
            value={form.checkCurrency}
            onChange={(e) => setField('checkCurrency', e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          >
            {SUPPORTED_CURRENCIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.code} — {t(`settings.currencies.${c.code}`, { defaultValue: c.name })}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('settings.exchangeRates')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('settings.exchangeRatesHelp', { currency: form.checkCurrency })}
          </Typography>
          <Stack spacing={1}>
            {(form.currencyRates || []).map((row, index) => (
              <Paper key={`rate-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <TextField select size="small" label={t('settings.fromCurrency')} value={row.fromCurrency || ''} onChange={(e) => updateRate(index, 'fromCurrency', e.target.value)} sx={{ minWidth: 120 }}>
                    {SUPPORTED_CURRENCIES.map((c) => <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>)}
                  </TextField>
                  <Typography color="text.secondary">→</Typography>
                  <TextField select size="small" label={t('settings.toCurrency')} value={row.toCurrency || form.checkCurrency} onChange={(e) => updateRate(index, 'toCurrency', e.target.value)} sx={{ minWidth: 120 }}>
                    {SUPPORTED_CURRENCIES.map((c) => <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label={t('settings.rate')} type="number" inputProps={{ min: 0, step: 0.0001 }} value={row.rate ?? ''} onChange={(e) => updateRate(index, 'rate', e.target.value)} sx={{ minWidth: 120 }} />
                  <IconButton color="error" onClick={() => removeRate(index)} aria-label={t('settings.removeRateAria')}><DeleteIcon /></IconButton>
                </Stack>
              </Paper>
            ))}
            <Button startIcon={<AddIcon />} onClick={addRate} size="small" sx={{ alignSelf: 'flex-start' }}>
              {t('settings.addExchangeRate')}
            </Button>
          </Stack>
        </Box>

        <TextField
          label={t('settings.maxExchangeRateChangeAmount')}
          value={form.maxExchangeRateChangeAmount}
          onChange={(e) => setField('maxExchangeRateChangeAmount', e.target.value)}
          type="number"
          inputProps={{ min: 0, step: 0.0001 }}
          helperText={t('settings.maxExchangeRateChangeAmountHelp')}
          size="small"
          sx={{ maxWidth: 320 }}
        />

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={() => onSave(buildCurrencySavePayload(form))}
            disabled={saving}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
          <Button variant="outlined" onClick={onReset} disabled={saving}>{t('common.reset')}</Button>
        </Stack>
      </Stack>
    </SettingsSectionCard>
  );
}

export default CurrencySettingsSection;
